import { useEffect, useRef, useCallback } from 'react';
import { Terminal as XTerm } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';
import { useAuth } from '../contexts/AuthContext';
import { API_URL, getAuthHeaders } from '../services/api';

interface TerminalProps {
	agentId: string;
	commandId: string | null;
	isActive: boolean;
	onClose?: () => void;
}

export default function Terminal({ agentId, commandId, isActive, onClose }: TerminalProps) {
	const { firebaseUser } = useAuth();
	const termRef = useRef<HTMLDivElement>(null);
	const xtermRef = useRef<XTerm | null>(null);
	const fitAddonRef = useRef<FitAddon | null>(null);
	const abortRef = useRef<AbortController | null>(null);

	// Send raw bytes (base64-encoded) to the agent via the stdin endpoint
	const sendInput = useCallback(async (data: string) => {
		if (!firebaseUser || !commandId) return;
		try {
			const idToken = await firebaseUser.getIdToken();
			const encoded = btoa(data);
			await fetch(`${API_URL}/api/v1/agents/${agentId}/logs/${commandId}/stdin`, {
				method: 'POST',
				headers: getAuthHeaders(idToken),
				body: JSON.stringify({ line: encoded }),
			});
		} catch (err) {
			console.error('[Terminal] Failed to send input:', err);
		}
	}, [agentId, commandId, firebaseUser]);

	// Send resize event
	const sendResize = useCallback(async (rows: number, cols: number) => {
		if (!firebaseUser || !commandId) return;
		try {
			const idToken = await firebaseUser.getIdToken();
			const resizePayload = JSON.stringify({ type: 'resize', rows, cols });
			const encoded = btoa(resizePayload);
			await fetch(`${API_URL}/api/v1/agents/${agentId}/logs/${commandId}/stdin`, {
				method: 'POST',
				headers: getAuthHeaders(idToken),
				body: JSON.stringify({ line: encoded }),
			});
		} catch (err) {
			console.error('[Terminal] Failed to send resize:', err);
		}
	}, [agentId, commandId, firebaseUser]);

	// Initialize xterm.js
	useEffect(() => {
		if (!termRef.current || !isActive) return;

		const term = new XTerm({
			cursorBlink: true,
			fontSize: 13,
			fontFamily: 'Menlo, Monaco, "Courier New", monospace',
			theme: {
				background: '#0f172a', // slate-950
				foreground: '#e2e8f0', // slate-200
				cursor: '#818cf8', // indigo-400
				selectionBackground: '#334155', // slate-700
			},
			convertEol: true,
		});

		const fitAddon = new FitAddon();
		term.loadAddon(fitAddon);

		term.open(termRef.current);
		fitAddon.fit();

		xtermRef.current = term;
		fitAddonRef.current = fitAddon;

		// Forward user keystrokes to the agent
		term.onData((data) => {
			sendInput(data);
		});

		// Forward resize events
		term.onResize(({ rows, cols }) => {
			sendResize(rows, cols);
		});

		// Watch container resizes
		const observer = new ResizeObserver(() => {
			fitAddon.fit();
		});
		observer.observe(termRef.current);

		return () => {
			observer.disconnect();
			term.dispose();
			xtermRef.current = null;
			fitAddonRef.current = null;
		};
	}, [isActive, sendInput, sendResize]);

	// Connect to SSE stream for output
	useEffect(() => {
		if (!commandId || !isActive || !firebaseUser) return;

		// Abort any existing connection
		if (abortRef.current) {
			abortRef.current.abort();
		}

		const controller = new AbortController();
		abortRef.current = controller;

		(async () => {
			try {
				const idToken = await firebaseUser.getIdToken();
				const url = `${API_URL}/api/v1/agents/${agentId}/logs/${commandId}`;

				const response = await fetch(url, {
					headers: {
						...getAuthHeaders(idToken),
						'Accept': 'text/event-stream',
					},
					signal: controller.signal,
				});

				if (!response.ok) {
					console.error('[Terminal] Failed to connect:', response.status);
					return;
				}

				const reader = response.body?.getReader();
				if (!reader) return;

				const decoder = new TextDecoder();
				let buffer = '';

				while (true) {
					const { done, value } = await reader.read();
					if (done) break;

					buffer += decoder.decode(value, { stream: true });
					const lines = buffer.split('\n');
					buffer = lines.pop() || '';

					for (const line of lines) {
						if (line.startsWith('data: ')) {
							try {
								const entry = JSON.parse(line.slice(6));
								const term = xtermRef.current;
								if (!term) continue;

								// base64-encoded PTY output (new terminal mode)
								if (entry.data) {
									const raw = atob(entry.data);
									term.write(raw);
								} else if (entry.line) {
									// Backward compat: plain text log lines
									// Try base64 decode first — the agent now
									// always base64-encodes PTY output via the
									// "line" field in logCallback
									try {
										const raw = atob(entry.line);
										term.write(raw);
									} catch {
										// Not valid base64, write as text
										term.writeln(entry.line);
									}
								}
							} catch {
								// Ignore unparseable SSE entries
							}
						}
					}
				}
			} catch (err: any) {
				if (err.name !== 'AbortError') {
					console.error('[Terminal] Stream error:', err);
				}
			}
		})();

		return () => {
			if (abortRef.current) {
				abortRef.current.abort();
				abortRef.current = null;
			}
		};
	}, [commandId, agentId, firebaseUser, isActive]);

	if (!isActive) return null;

	return (
		<div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
			{/* Header */}
			<div className="px-4 py-3 bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
				<div className="flex items-center space-x-3">
					<div className="flex items-center space-x-2">
						<div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
						<span className="text-sm font-medium text-slate-700 dark:text-slate-300">
							Terminal
						</span>
					</div>
					{commandId && (
						<span className="text-xs text-slate-500 dark:text-slate-400 font-mono">
							{commandId.slice(0, 8)}
						</span>
					)}
				</div>
				{onClose && (
					<button
						onClick={onClose}
						className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
					>
						<svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
						</svg>
					</button>
				)}
			</div>

			{/* Terminal */}
			<div
				ref={termRef}
				className="h-80"
				style={{ padding: '4px' }}
			/>
		</div>
	);
}
